/**
 * @file     components.ts
 * @readme   ../../CLAUDE.md
 * @purpose  English strings for shared UI components (hero section, settings panel, password gate, etc.).
 */
// ─── New Components (added 2026-03-15) ───────────────────────────
export const components = {
  "startHereCta": {
    "headline": "Start with what you need",
    "subtext": "Share what you want and get a shortlist that fits.",
    "button": "Build my shortlist \u2192"
  },
  "videoSection": {
    "playVideo": "Play video",
    "playVideoWithTitle": "Play video: {{title}}",
    "iframeTitle": "Video"
  },
  "heroSection": {
    "previewEyebrow": "Get started",
    "previewHeadline": "Pick where to start.",
    "previewTags": {
      "buyer": "Buying",
      "seller": "Selling",
      "tenants": "Find Tenants"
    }
  },
  "select": {
    "placeholder": "Select an option"
  },
  "sortableDataTable": {
    "emptyTitle": "No data available",
    "emptyDescription": "Adjust your filters or add new records to populate this table.",
    "pagination": {
      "rowsPerPage": "Rows per page:",
      "displayedRows": "{{from}}-{{to}} of {{count}}",
      "actions": {
        "first": "Go to first page",
        "last": "Go to last page",
        "next": "Go to next page",
        "previous": "Go to previous page"
      }
    }
  },
  "chartPlaceholder": {
    "description": "Use this surface for pipeline volume, engagement, or conversion trends.",
    "months": {
      "jan": "Jan",
      "feb": "Feb",
      "mar": "Mar",
      "apr": "Apr",
      "may": "May",
      "jun": "Jun"
    }
  },
  "featureComparisonChecklist": {
    "header": {
      "feature": "Feature"
    },
    "states": {
      "included": "Included",
      "limited": "Limited",
      "notIncluded": "Not included"
    }
  },
  "pageDecorations": {
    "shared": {
      "problemEyebrow": "Common friction",
      "educationEyebrow": "Good to know"
    },
    "home": {
      "metrics": {
        "title": "What you get from this approach",
        "description": "Every engagement is built around one goal: a high-confidence decision, made with honest information and without unnecessary pressure.",
        "paths": {
          "value": "~5 min",
          "label": "To choose the right journey",
          "trend": "Short enough to finish now, specific enough to be useful"
        },
        "workflow": {
          "value": "One",
          "label": "One advisor. Direct contact.",
          "trend": "No handoffs. No assigned assistants. No team confusion."
        },
        "feed": {
          "value": "Weekly",
          "label": "Vancouver market clarity brief",
          "trend": "What changed, what it means, and what to watch next"
        },
        "local": {
          "value": "Local",
          "label": "Vancouver-specific guidance only",
          "trend": "Not national commentary repackaged for your city"
        }
      },
      "faq": {
        "title": "Questions people usually ask first",
        "description": "",
        "items": {
          "paths": {
            "question": "I'm just starting to look. Is it too early to reach out?",
            "answer": "Not at all. The best conversations start well before someone is ready to move. Getting clarity early means you avoid rushing later."
          },
          "market": {
            "question": "How is working with you different?",
            "answer": "Data and honesty come first, not urgency. If something is not a good fit, you will hear it. Tools like the neighbourhood explorer and mortgage calculator let you do your own research before you ever reach out."
          },
          "speed": {
            "question": "What happens after I fill this out?",
            "answer": "Your answers are reviewed personally, with specific next steps based on your situation. No generic emails, no assistant."
          },
          "cost": {
            "question": "Does this cost anything?",
            "answer": "No. The tools, the guided questions, and the first conversation cost you nothing. No commitment, no pressure."
          },
          "timeline": {
            "question": "What if I'm not ready to buy or sell yet?",
            "answer": "That is actually the best time to start. Understanding your options before you are under pressure leads to better decisions and fewer regrets."
          }
        }
      }
    },
    "flow": {
      "dealFinder": {
        "metrics": {
          "matching": {
            "value": "Three",
            "label": "Clear service journeys",
            "trend": "Buying, selling, and tenant placement each start differently"
          },
          "timeline": {
            "value": "One",
            "label": "Guided intake shell",
            "trend": "Shared structure keeps the first step easy to understand"
          },
          "clarity": {
            "value": "Clear",
            "label": "The next step is matched to your objective",
            "trend": "Less friction than starting with the wrong form"
          },
          "handoff": {
            "value": "Direct",
            "label": "The next step stays connected to your answers",
            "trend": "No need to re-explain the basics"
          }
        },
        "faq": {
          "title": "What to expect from the guided start",
          "description": "This entry flow is intentionally short. The point is to clarify direction before deeper conversations.",
          "items": {
            "time": {
              "question": "How long does this take?",
              "answer": "Usually a few minutes. It focuses on the questions that meaningfully change which journey and next step make sense."
            },
            "whatHappens": {
              "question": "What happens after I submit?",
              "answer": "The response is based on your stated goals, timeline, and constraints so the follow-up is more specific from the start."
            },
            "fit": {
              "question": "Should I use this if I am still early?",
              "answer": "Yes. It is designed for people who want a structured starting point, not just people who are ready to move immediately."
            }
          }
        }
      },
      "homeValue": {
        "metrics": {
          "range": {
            "value": "Current",
            "label": "Value ranges are framed for today's market",
            "trend": "Useful before committing to a listing timeline"
          },
          "strategy": {
            "value": "Strategy",
            "label": "Pricing and timing are reviewed together",
            "trend": "Better for sellers weighing multiple options"
          },
          "prep": {
            "value": "Prep",
            "label": "The workflow surfaces readiness questions early",
            "trend": "Helps separate urgent tasks from optional ones"
          },
          "timing": {
            "value": "Timing",
            "label": "The next step is tied to your constraints",
            "trend": "Built for sellers deciding now versus later"
          }
        },
        "faq": {
          "title": "Before you request a home value review",
          "description": "These answers help set expectations for what this page is built to do.",
          "items": {
            "estimate": {
              "question": "Is this meant to replace a full valuation?",
              "answer": "No. It is a practical starting point for pricing clarity and planning, not a substitute for deeper property-specific analysis."
            },
            "prep": {
              "question": "Can I use this before I finish preparing the home?",
              "answer": "Yes. Many sellers use it to understand what work matters first and what can likely wait."
            },
            "timeline": {
              "question": "What if I am not sure whether I want to sell now?",
              "answer": "That is a normal use case. The goal is to clarify range, timing pressure, and the trade-offs of acting now versus holding."
            }
          }
        }
      }
    },
    "blog": {
      "metrics": {
        "featured": {
          "label": "Featured reads surfaced first",
          "trend": "Start with the highest-signal commentary"
        },
        "feed": {
          "label": "Combined stories in the live feed",
          "trend": "Multiple local voices in one place"
        },
        "sources": {
          "label": "Sources represented across the feed",
          "trend": "Broader context for buyers and sellers"
        },
        "sidebar": {
          "label": "Additional posts ready to skim",
          "trend": "Open the sidebar when you want a faster scan"
        }
      },
      "mobileSidebar": {
        "button": "Open feed sidebar",
        "title": "More from the feed",
        "description": "Use the sidebar to scan additional posts without losing your place in the main list."
      }
    },
    "brand": {
      "servicePaths": {
        "title": "Choose the right starting point",
        "description": "These journeys are intentionally simple. Pick the one that matches your current decision and move into the most relevant next step for buying, selling, or tenant placement.",
        "plans": {
          "buyer": {
            "name": "Buyer planning",
            "price": "Complimentary",
            "cadence": "first step",
            "description": "Best for people who want clarity on search direction, trade-offs, and timing before acting.",
            "features": {
              "strategy": "Clarify budget, risk, and search logic",
              "search": "Refine neighborhoods and property fit",
              "timeline": "Match the next step to your timing"
            },
            "cta": "Start buyer request",
            "badge": "Good starting point"
          },
          "seller": {
            "name": "Seller planning",
            "price": "Complimentary",
            "cadence": "first step",
            "description": "Best for owners sorting through pricing, preparation, and timing decisions before listing.",
            "features": {
              "pricing": "Review current pricing posture",
              "prep": "Prioritize the work that matters most",
              "launch": "Align launch timing with real constraints"
            },
            "cta": "Start seller request",
            "badge": "Most direct"
          },
          "tenantPlacement": {
            "name": "Tenant placement",
            "price": "Complimentary",
            "cadence": "owner planning",
            "description": "Best for owners who want help leasing the property without stepping into full property management.",
            "features": {
              "positioning": "Clarify rental positioning and pricing",
              "screening": "Define tenant-fit and showing approach",
              "timing": "Align the lease step with your timing"
            },
            "cta": "Start tenant request",
            "badge": "Owner-focused"
          }
        }
      },
      "comparison": {
        "title": "How the starting points differ",
        "description": "Each journey serves a different job. This table is meant to make the distinctions obvious.",
        "rows": {
          "strategyCall": "Strategy call framing",
          "propertyMatch": "Property Match guidance",
          "valueReview": "Home value review",
          "tenantPlacement": "Tenant placement plan"
        }
      }
    },
    "landingFaq": {
      "aboutRay": {
        "title": "About the advisory approach",
        "description": "These are the questions that usually come up once people understand the positioning.",
        "items": {
          "one": {
            "question": "What makes this approach different from a typical sales process?",
            "answer": "The emphasis is on clarity first. Education, scenario comparison, and downside awareness come before pressure or urgency."
          },
          "two": {
            "question": "Can I reach out if I am still early in the process?",
            "answer": "Yes. Early-stage conversations are often the most useful because they help shape better decisions before the stakes rise."
          },
          "three": {
            "question": "Is this only for buyers?",
            "answer": "No. The framework applies to buyers, sellers, relocations, and people who simply need better market context."
          }
        }
      },
      "contact": {
        "title": "Before you reach out",
        "description": "These answers are meant to make the contact page feel lower-pressure and easier to act on.",
        "items": {
          "one": {
            "question": "What should I include in my message?",
            "answer": "The most useful details are your goal, timeline, budget range or property type, and the main question you are trying to solve."
          },
          "two": {
            "question": "How quickly should I expect a reply?",
            "answer": "You should expect a clear response and practical next step rather than a long automated sequence."
          },
          "three": {
            "question": "What if I am not ready for a transaction?",
            "answer": "That is fine. Many conversations start well before someone is ready to buy or sell."
          }
        }
      },
      "marketUpdate": {
        "title": "Questions about the market brief",
        "description": "The market page works best when it is used as a decision aid, not just a headline scan.",
        "items": {
          "one": {
            "question": "How often should I check the market update?",
            "answer": "Weekly is usually enough for most people. The goal is to track signal shifts, not react to every daily headline."
          },
          "two": {
            "question": "Can this replace local property-level research?",
            "answer": "No. It helps frame the environment, but property quality, pricing, and fit still need to be assessed in context."
          },
          "three": {
            "question": "Who is this most useful for?",
            "answer": "Buyers, sellers, and owners who want a disciplined view of leverage, timing, and risk in Vancouver."
          }
        }
      },
      "buyerPlanning": {
        "title": "Before you use the guide or tool",
        "description": "These pages are designed to reduce decision fatigue by giving you a clearer frame before acting.",
        "items": {
          "one": {
            "question": "Should I finish every guide before making contact?",
            "answer": "No. Use the sections that solve your immediate question, then move into a more direct conversation when the next step becomes clear."
          },
          "two": {
            "question": "Are these pages only for first-time buyers?",
            "answer": "No. The frameworks are especially helpful for first-time buyers, but they also work for anyone who wants a more rational decision process."
          },
          "three": {
            "question": "What if my situation spans more than one topic?",
            "answer": "That is normal. Start with the page closest to your main decision, then use the related routes to compare trade-offs."
          }
        }
      },
      "sellerPlanning": {
        "title": "Before you make a seller decision",
        "description": "Seller pages are meant to make pricing, timing, and readiness easier to think through without pressure.",
        "items": {
          "one": {
            "question": "Do I need to decide whether to list before reaching out?",
            "answer": "No. Many sellers use these pages because they are still deciding between selling now, later, or not at all."
          },
          "two": {
            "question": "Will this help if my home needs work first?",
            "answer": "Yes. Preparation questions are part of the decision, and they often change the best launch strategy."
          },
          "three": {
            "question": "Is the goal to get me to list quickly?",
            "answer": "No. The stronger goal is to help you understand whether listing now is logical in your specific context."
          }
        }
      }
    },
    "marketUpdate": {
      "title": "A quick market intelligence board",
      "description": "This layer adds a compact dashboard to the written update so the main signals are easier to scan.",
      "metrics": {
        "cadence": {
          "value": "Weekly",
          "label": "Market brief cadence",
          "trend": "Structured to keep headline noise in check"
        },
        "signals": {
          "value": "4 signals",
          "label": "Inventory, leverage, timing, and demand",
          "trend": "Read together instead of in isolation"
        },
        "watchlist": {
          "value": "Sortable",
          "label": "Segment watchlist for quick scanning",
          "trend": "Useful when you want a faster comparison pass"
        },
        "context": {
          "value": "Local",
          "label": "Built around Vancouver trade-offs",
          "trend": "Focused on decision quality, not hype"
        }
      },
      "sidebar": {
        "summary": {
          "eyebrow": "What to monitor",
          "title": "Read the market as a sequence",
          "body": "Start with broad conditions, then move to leverage, pricing posture, and what that means for your next move."
        },
        "filters": {
          "eyebrow": "How to use it",
          "title": "Scan the table before the headlines",
          "body": "The sortable rows are there to help you compare segments quickly before diving deeper into any one narrative."
        }
      },
      "chart": {
        "title": "Signal momentum across the brief",
        "description": "Use this chart to see how the major themes are stacking rather than treating each one as an isolated headline."
      },
      "table": {
        "title": "Segments to keep on the board",
        "description": "These are example watchlist slices that work well as a weekly scan before you move into property-level research.",
        "columns": {
          "segment": "Segment",
          "signal": "Signal",
          "posture": "Posture",
          "nextStep": "Next step"
        },
        "rows": {
          "westSide": {
            "segment": "West Side condos",
            "signal": "Inventory is building faster than urgency",
            "posture": "Watch for leverage",
            "nextStep": "Track repricing and stale listings"
          },
          "eastVan": {
            "segment": "East Van townhomes",
            "signal": "Quality stock still draws attention",
            "posture": "Stay selective",
            "nextStep": "Compare condition before stretching budget"
          },
          "burnaby": {
            "segment": "Burnaby family homes",
            "signal": "Rate sensitivity is slowing some buyers",
            "posture": "Look for patience",
            "nextStep": "Monitor seller timing pressure"
          },
          "presale": {
            "segment": "Presale assignments",
            "signal": "Context matters more than headline discounts",
            "posture": "Filter carefully",
            "nextStep": "Check carrying costs and completion timing"
          }
        }
      }
    },
    "funnelFaq": {
      "buyer": {
        "title": "Common questions before starting",
        "description": "Answers to what first-time buyers usually ask before they commit to a next step.",
        "items": {
          "one": {
            "question": "I am not ready to buy for another year. Is this useful now?",
            "answer": "Yes - and earlier is usually better. Understanding the decision framework before you are under pressure means the waiting period works for you instead of creating anxiety. Property Match is a good starting point even twelve months out."
          },
          "two": {
            "question": "What is the difference between pre-approval and actual affordability?",
            "answer": "Pre-approval tells you the maximum a lender will extend. Affordability is the payment you can sustain without restructuring your life. Most first-time buyers discover the gap between these two numbers too late. The guide addresses this first."
          },
          "three": {
            "question": "I have already been looking at listings. Is this still useful?",
            "answer": "Yes. Exposure to listings without a framework usually creates anchoring bias and comparison fatigue. This guide helps you reset with clearer criteria before the next offer."
          },
          "four": {
            "question": "Does this cover strata documents and condo-specific risk?",
            "answer": "Yes. The guide includes a section on what to review in strata documents before making an offer - minutes, depreciation reports, and operating fund balances - and what the red flags look like in practice."
          },
          "five": {
            "question": "What does Property Match actually do?",
            "answer": "Property Match is a short intake flow - about five minutes - that takes your budget, timeline, priorities, and constraints and turns them into specific search criteria and a recommended next step. There is no pressure and no sales cycle."
          }
        }
      },
      "condo": {
        "title": "Condo buyer guide questions",
        "description": "Most condo mistakes happen when buyers underestimate building risk. These answers reinforce what to look for.",
        "items": {
          "one": {
            "question": "Do I need every document before I start using this guide?",
            "answer": "No. The guide helps you know which documents and red flags matter before you are deep in review."
          },
          "two": {
            "question": "Why is building quality emphasized so much?",
            "answer": "Because layout, finish, and price do not compensate for weak maintenance, expensive future work, or poor governance."
          },
          "three": {
            "question": "Can this help if I am comparing multiple buildings?",
            "answer": "Yes. That is one of the best use cases because the framework makes the trade-offs easier to compare consistently."
          }
        }
      },
      "relocation": {
        "title": "Relocation guide questions",
        "description": "Relocation decisions are usually better when the practical trade-offs are surfaced early.",
        "items": {
          "one": {
            "question": "Should I book a call before I know the exact neighborhood?",
            "answer": "Yes. A relocation conversation is often most useful before you over-commit to one area or price band."
          },
          "two": {
            "question": "Is this guide mainly about homes or about lifestyle fit?",
            "answer": "It is both. The best decisions come from matching housing, commute, budget, and day-to-day fit together."
          },
          "three": {
            "question": "Can I use this if my move timing is still loose?",
            "answer": "Yes. The framework is helpful precisely because it can handle uncertainty and turn it into clearer priorities."
          }
        }
      },
      "opportunities": {
        "title": "Opportunity guide questions",
        "description": "Use the opportunity funnel as a filter, not as a shortcut into rushed decisions.",
        "items": {
          "one": {
            "question": "What qualifies as a real opportunity?",
            "answer": "A real opportunity has context behind it: leverage, pricing posture, timing, and property quality all need to line up."
          },
          "two": {
            "question": "Are price drops enough on their own?",
            "answer": "No. Discounts can be noise if the quality, location, or carrying costs do not justify the trade."
          },
          "three": {
            "question": "How should I use the alerts and watchlist together?",
            "answer": "Use alerts to notice candidate situations, then use the watchlist to compare them with a calmer and more structured lens."
          }
        }
      }
    },
    "opportunities": {
      "title": "Opportunity board",
      "description": "This adds a compact scan layer to the opportunity page so leverage signals are easier to compare.",
      "metrics": {
        "windows": {
          "value": "Weekly",
          "label": "Opportunity windows reviewed in rhythm",
          "trend": "Useful for staying patient between launches"
        },
        "filters": {
          "value": "4 filters",
          "label": "Leverage, quality, timing, and context",
          "trend": "A better filter than price drops alone"
        },
        "context": {
          "value": "Curated",
          "label": "Signals framed with decision context",
          "trend": "Designed to cut false urgency"
        },
        "alerts": {
          "value": "Watchlist",
          "label": "Examples ready for quick comparison",
          "trend": "Helpful before you commit to deeper review"
        }
      },
      "sidebar": {
        "overview": {
          "eyebrow": "What matters",
          "title": "Leverage needs context",
          "body": "The best opportunities tend to pair pricing flexibility with solid fundamentals, not just visible discounts."
        },
        "filters": {
          "eyebrow": "How to scan",
          "title": "Sort by posture first",
          "body": "Start with the posture column to separate watchlist candidates from situations that only look attractive at first glance."
        }
      },
      "chart": {
        "title": "Opportunity signal mix",
        "description": "This placeholder chart shows how the higher-signal themes can cluster over time."
      },
      "table": {
        "title": "Example opportunity watchlist",
        "description": "Use a list like this to separate worthwhile leads from listings that only look like a deal on the surface.",
        "columns": {
          "segment": "Segment",
          "signal": "Signal",
          "posture": "Posture",
          "nextStep": "Next step"
        },
        "rows": {
          "staleCondos": {
            "segment": "Stale central condos",
            "signal": "Longer days on market",
            "posture": "Worth monitoring",
            "nextStep": "Check seller flexibility and building quality"
          },
          "familyHomes": {
            "segment": "Family homes with timing pressure",
            "signal": "Life-event driven timelines",
            "posture": "Compare closely",
            "nextStep": "Look for preparation gaps and pricing resets"
          },
          "presaleAssignments": {
            "segment": "Presale assignments",
            "signal": "Completion timing creates urgency",
            "posture": "Filter hard",
            "nextStep": "Review carrying cost assumptions"
          },
          "valueRenovations": {
            "segment": "Homes with cosmetic drag",
            "signal": "Presentation is suppressing attention",
            "posture": "Potential upside",
            "nextStep": "Validate renovation scope before acting"
          }
        }
      }
    },
    "notFound": {
      "description": "The route you requested is not here, but the main guidance paths are still easy to reach from here.",
      "actions": {
        "buyer": "Start buyer journey",
        "contact": "Contact"
      },
        "faq": {
          "title": "Useful places to go next",
          "description": "If you landed here from an old link or a mistyped URL, these are usually the fastest recovery paths.",
          "items": {
            "home": {
            "question": "How do I get back to the main navigation?",
            "answer": "Use Go Home to return to the landing page and choose the Buy, Sell, or Find Tenants journey from the top of the experience."
          },
          "buyer": {
            "question": "I was trying to find buyer guidance. Where should I start?",
            "answer": "The buyer journey and the first-time buyer guide are the best next stops if you want structured help without jumping straight into listings."
          },
          "contact": {
            "question": "What if I wanted to talk to someone directly?",
            "answer": "The contact page is the fastest route if you already know you want a conversation instead of more self-serve guidance."
          }
        }
      }
    }
  }
} as const;
